/** Is the deck beside the stage or under it? (UX §6.1)
 *
 * The stylesheet decides the columns with a container query; this decides the *behaviour* that
 * follows from them — when the deck is under the stage, opening it collapses the stage to a
 * sticky three-line strip so the timer is never lost. Both read the same number, in rem, so
 * they can never disagree: at 1280 px with the magnifier at 200% the root is 32 px, the window
 * is 40rem, and both say "one column".
 */
import { useEffect, useState } from "react";

/** Keep this equal to the `@container live (min-width: …)` threshold in styles.css. */
export const DECK_BESIDE_REM = 56;

function rootPx(): number {
  const size = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(size) && size > 0 ? size : 16;
}

export function useDeckBeside(el: HTMLElement | null): boolean {
  const [beside, setBeside] = useState(false);
  useEffect(() => {
    if (!el) return;
    const measure = () => setBeside(el.clientWidth / rootPx() >= DECK_BESIDE_REM);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // The magnifier changes the root font size without changing the element's width, so the
    // ratio moves even though nothing resized.
    const mo = new MutationObserver(measure);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["style", "class"] });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [el]);
  return beside;
}
